import {
  DeleteInactiveUserMutation,
  DeleteInactiveUserMutationVariables,
  PendingUsersQuery,
  PendingUsersQueryVariables,
  ResendInvitationMutation,
  ResendInvitationMutationVariables,
  RevokeInvitationMutation,
  RevokeInvitationMutationVariables,
} from "@/__generated__/graphql";
import {
  DELETE_INACTIVE_USER_MUTATION,
  RESEND_INVITATION_MUTATION,
  REVOKE_INVITATION_MUTATION,
} from "@/components/SettingsSidepanel/settings.graphql.ts";
import {
  PendingInvitationProps,
  User,
} from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import SearchBar from "@/components/ui/search-bar.tsx";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import {
  USERS_QUERY,
  PENDING_USERS_QUERY,
} from "@/graphql/queries/common/UsersQuery.ts";
import {
  INVITE_TOKEN_TIMEOUT_SECONDS,
  userRoles,
} from "@/helpers/constants.ts";
import { useMutation, useQuery } from "@apollo/client";
import { captureException } from "@sentry/react";
import { Info, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { useDebounce } from "use-debounce";
import { InfiniteScroll } from "@/components/common/InfiniteScroll.tsx";
import { Loader } from "@/components/common/Loader.tsx";

const PAGE_SIZE = 20;

const PendingInvitations: React.FC<PendingInvitationProps> = ({
  officeId,
  onBack,
  onRequestClose,
}) => {
  const { currentUser } = useUserContext();
  const [searchEmail, setSearchEmail] = useState("");
  const [debouncedSearch] = useDebounce(searchEmail, 300);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { loading, error, data, previousData, fetchMore, refetch } = useQuery<
    PendingUsersQuery,
    PendingUsersQueryVariables
  >(PENDING_USERS_QUERY, {
    variables: {
      officeId,
      search: debouncedSearch.trim() || null,
      first: PAGE_SIZE,
      offset: 0,
    },
    fetchPolicy: "cache-and-network",
  });

  const [removeInvitationMutation] = useMutation<
    DeleteInactiveUserMutation,
    DeleteInactiveUserMutationVariables
  >(DELETE_INACTIVE_USER_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }, { query: PENDING_USERS_QUERY }],
  });

  const [resendInvitationMutation] = useMutation<
    ResendInvitationMutation,
    ResendInvitationMutationVariables
  >(RESEND_INVITATION_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }, { query: PENDING_USERS_QUERY }],
  });

  const [revokeInvitationMutation] = useMutation<
    RevokeInvitationMutation,
    RevokeInvitationMutationVariables
  >(REVOKE_INVITATION_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }, { query: PENDING_USERS_QUERY }],
  });

  if (!currentUser) return null;

  const currentData = data ?? previousData;
  const pendingUsers: User[] = (currentData?.pendingUsers ?? []) as User[];
  const totalCount = currentData?.pendingUsersCount ?? 0;
  const hasMore = pendingUsers.length < totalCount;

  const loadMore = async () => {
    if (loading || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          offset: pendingUsers.length,
          first: PAGE_SIZE,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;
          return {
            ...previousResult,
            pendingUsers: [
              ...(previousResult.pendingUsers || []),
              ...(fetchMoreResult.pendingUsers || []),
            ],
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (error && !currentData) {
    return (
      <div className="flex flex-col h-full">
        <SettingsSheetHeader
          title="Pending Invitations"
          onRequestClose={onRequestClose}
          onBack={onBack}
        />
        <div className="text-red-500 text-center py-6">
          Error Loading Pending Invitations: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SettingsSheetHeader
        title="Pending Invitations"
        onRequestClose={onRequestClose}
        onBack={onBack}
      />
      <div className="flex flex-col gap-4 p-4 rounded-2xl border flex-1 border-accent-foreground overflow-auto">
        <div className="sticky top-0 z-10 flex items-center gap-2 rounded-2xl bg-[#FFAB4D33] p-4">
          <div className="rounded-full bg-yellow-600 p-2">
            <Info className="size-5 text-white" />
          </div>
          <p className="text-service-3d-tracking text-sm">
            Invitations expire after 3 days. You can resend an invitation after
            it expires.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 bg-primary">
          <SearchBar
            placeholder="Search by email..."
            searchText={searchEmail}
            setSearchText={setSearchEmail}
            containerClassName="w-full max-w-xs"
            className="h-9 text-xs"
          />
        </div>

        <div className="flex-1 overflow-auto">
          {loading && !currentData ? (
            <div className="flex items-center justify-center py-12">
              <Loader />
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {debouncedSearch
                ? "No pending invitations matching your email search."
                : "No pending invitations."}
            </div>
          ) : (
            <InfiniteScroll
              hasMore={hasMore}
              loader={
                <div className="py-3 text-center text-sm text-muted-foreground">
                  <Loader size={18} />
                </div>
              }
              loadMore={loadMore}
            >
              <div className="flex flex-col gap-4">
                {pendingUsers.map((user) => {
                  const isExpired =
                    typeof user.invitationExpiresInSeconds === "number" &&
                    user.invitationExpiresInSeconds < 0;

                  return (
                    <div
                      key={user.id}
                      className="flex flex-wrap items-start gap-4 rounded-lg bg-button p-4 shadow-sm"
                    >
                      <Button
                        disabled={!isExpired && !user.invitationRevokedBy}
                        size="icon"
                        title="Remove Invitation"
                        onClick={async () => {
                          try {
                            const result = await removeInvitationMutation({
                              variables: { userId: user.id },
                            });
                            if (result.data?.deleteInactiveUser?.success) {
                              refetch();
                            } else {
                              toast.error(
                                result.data?.deleteInactiveUser?.message ??
                                  "Failed to remove invitation.",
                              );
                            }
                          } catch (err) {
                            captureException(err);
                            toast.error("Error removing invitation.");
                          }
                        }}
                      >
                        <X />
                      </Button>

                      <div className="min-w-0 flex-1">
                        <div className="text-base font-normal text-secondary-foreground flex items-center gap-2">
                          <p>{user.email}</p>
                          <span className="rounded bg-border p-1 text-sm">
                            {userRoles[user.role]}
                          </span>
                        </div>
                        <p className="text-xs">
                          {user.invitationRevokedBy ? (
                            <>
                              <span className="font-semibold text-destructive">
                                REVOKED
                              </span>
                              {Boolean(
                                user.invitationRevokedBy.firstName ||
                                user.invitationRevokedBy.lastName,
                              ) && (
                                <span>
                                  {" "}
                                  by {user.invitationRevokedBy.firstName}{" "}
                                  {user.invitationRevokedBy.lastName}
                                </span>
                              )}
                            </>
                          ) : isExpired ? (
                            <span className="font-semibold text-destructive">
                              EXPIRED
                            </span>
                          ) : (
                            <>
                              <span className="font-semibold text-green-500">
                                INVITED
                              </span>
                              {Boolean(
                                user.inviter?.firstName ||
                                user.inviter?.lastName,
                              ) && (
                                <span>
                                  {" "}
                                  by {user.inviter?.firstName}{" "}
                                  {user.inviter?.lastName}
                                </span>
                              )}
                              {typeof user.invitationExpiresInSeconds ===
                                "number" && (
                                <>
                                  &nbsp;(Expires in&nbsp;
                                  {(
                                    user.invitationExpiresInSeconds /
                                    (INVITE_TOKEN_TIMEOUT_SECONDS / 72)
                                  ).toFixed(0)}
                                  &nbsp;hours)
                                </>
                              )}
                            </>
                          )}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {user.invitationRevokedBy ? (
                          <Button
                            disabled
                            className="border border-red-500 text-red-500"
                          >
                            Revoked
                          </Button>
                        ) : (
                          <>
                            <Button
                              className="bg-link text-white hover:bg-link/80"
                              onClick={async () => {
                                try {
                                  const result = await resendInvitationMutation(
                                    {
                                      variables: { userId: user.id },
                                    },
                                  );
                                  if (result.data?.resendInvitation?.success) {
                                    refetch();
                                  } else {
                                    toast.error(
                                      result.data?.resendInvitation?.message ??
                                        "Failed to resend invitation.",
                                    );
                                  }
                                } catch (err) {
                                  captureException(err);
                                  toast.error("Error resending invitation.");
                                }
                              }}
                            >
                              Re-send
                            </Button>

                            {!isExpired && (
                              <Button
                                className="bg-red-500 text-white hover:bg-red-500/80"
                                onClick={async () => {
                                  try {
                                    const result =
                                      await revokeInvitationMutation({
                                        variables: { userId: user.id },
                                      });
                                    if (
                                      result.data?.revokeInvitation?.success
                                    ) {
                                      refetch();
                                    } else {
                                      toast.error(
                                        result.data?.revokeInvitation
                                          ?.message ??
                                          "Failed to revoke invitation.",
                                      );
                                    }
                                  } catch (err) {
                                    captureException(err);
                                    toast.error("Error revoking invitation.");
                                  }
                                }}
                              >
                                Revoke
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingInvitations;
